import React, { useEffect, useRef, useState } from 'react';
import { styled } from '@glitz/react';
import { H3, Paragraph, ResponsiveSize } from 'Shared/SharedComponents/atoms/Typography';
import { ButtonHeight, ButtonTheme, LinkButton } from 'Shared/TextButtons';
import OpeningHours from './OpeningHours';
import * as style from 'Shared/Style';

type StoreInformationLabelsType = {
  openingHours: string;
  openingHoursExceptions: string;
  contact: string;
  findStore: string;
};

type StoreInformationPropType = {
  labels: StoreInformationLabelsType;
  openingHours: { [item: string]: string };
  openingHoursExceptions: { [item: string]: string };
  phoneNumber: string;
  directionsLink: string;
  show: boolean;
};

const OpeningHoursHeading = styled(H3, {
  padding: { bottom: '6px' }
})

const OpeningHoursExceptionsHeading = styled(H3, {
  padding: { bottom: '6px' }
})

const ContactWrap = styled.div({
  display: 'flex', 
  margin: { top: '30px' },
  flexDirection: 'column', 
  ['@media ' + style.mediaMinLarge]: {
    flexDirection: 'row', 
  },
})

const OpeningHoursOuter = styled.div({
  display: 'flex'
})

const OpeningHoursWrap = styled.div({
  ['@media ' + style.mediaMinLarge]: {
    flex: { basis: '50%' },
    paddingRight: '60px',
  },
})

const ContactData = styled.div({
  ['@media ' + style.mediaMinLarge]: {
    flex: { basis: '50%' },
    paddingRight: '60px',
  },
})

const ContactDataText = styled(Paragraph, {
  padding: { top: '8px' } 
})

const ContactButton = styled.div({
  ['@media ' + style.mediaUpToLarge]: {
    marginTop: '24px',
  },
  ['@media ' + style.mediaMinLarge]: {
    flex: { basis: '50%' },
  },
})

function StoreInformation({
  labels,
  openingHours,
  openingHoursExceptions,
  phoneNumber,
  directionsLink,
  show,
}: StoreInformationPropType) {

  const [heightTest, setHeight] = useState({shouldTranslateHeight: false, currentHeight: 0});
  const [isOpen, setIsOpen] = useState(show);

  useEffect(() => {
    if (isOpen !== show) {
      setHeight({shouldTranslateHeight: true, currentHeight: 0});
    } else {
      setHeight({shouldTranslateHeight: false, currentHeight: 0});
    }

    setIsOpen(show);
  }, []);

  const updateOffsetHeight = (ref: HTMLElement) => {
    if(show) {
      if (ref) {
        // ref to prevent browser freeze
        requestAnimationFrame(() => {
          const height = ref.offsetHeight;
          if (Math.abs(heightTest.currentHeight - height) > 2) {
            setHeight({shouldTranslateHeight: true, currentHeight: height});
          }
        });
      }
    }
  }

  return (
    <styled.Div
      css={{
        overflow: 'hidden',
        height: show ? `${heightTest.currentHeight}px` : '0px',
        ...(heightTest.shouldTranslateHeight && {
          ...style.transition({
            property: 'height',
            duration: `${300}ms`,
            willChange: true,
          }),
        }),
        
      }}
    >
      <styled.Div ref={ref => updateOffsetHeight(ref)} >
        <OpeningHoursOuter className="store-availability-modal__opening-hours">
          {openingHours && (
            <OpeningHoursWrap>
              <OpeningHoursHeading upperCase fontSize={ResponsiveSize.D14_M14}>
                {labels.openingHours}
              </OpeningHoursHeading>
              <OpeningHours openingHours={openingHours} />
            </OpeningHoursWrap>
          )}
          {openingHoursExceptions && (
            <OpeningHoursWrap>
              <OpeningHoursExceptionsHeading upperCase fontSize={ResponsiveSize.D14_M14}>
                {labels.openingHoursExceptions}
              </OpeningHoursExceptionsHeading>
              <OpeningHours openingHours={openingHoursExceptions} />
            </OpeningHoursWrap>
          )}
        </OpeningHoursOuter>
        <ContactWrap className="store-availability-modal__contact-information">
          <ContactData>
            <H3 upperCase fontSize={ResponsiveSize.D14_M14}>
              {labels.contact}
            </H3>
            <ContactDataText fontSize={ResponsiveSize.D14_M14_L130}>
              {phoneNumber}
            </ContactDataText>
          </ContactData>
          <ContactButton className="store-availability-modal__find-store">
            <LinkButton
              text={labels.findStore}
              to={directionsLink}
              theme={ButtonTheme.BlackToWhite}
              height={ButtonHeight.Small}
              padding={{ x: 12 }}
            />
          </ContactButton>
        </ContactWrap>
      </styled.Div>
    </styled.Div>
  );
}

export default StoreInformation;
